import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Dns as ServersIcon,
  People as ClientsIcon,
  PersonOutline as UsersIcon,
  DesktopWindows as SessionsIcon,
  InstallDesktop as SetupIcon,
} from '@mui/icons-material';
import api from '../../api/axios';
import { DashboardStats } from '../../types';
import { useTranslation } from 'react-i18next';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, bgColor }) => (
  <Card
    sx={{
      borderRadius: 3,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      },
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant="body2"
            sx={{ color: '#78909c', fontWeight: 500, mb: 0.5 }}
          >
            {title}
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, color: '#263238' }}
          >
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: bgColor,
          }}
        >
          {React.cloneElement(icon as React.ReactElement, {
            sx: { fontSize: 32, color: color },
          })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get<DashboardStats>('/admin/dashboard');
      setStats(response.data);
    } catch (err: any) {
      const message = err.response?.data?.message || t('common.error');
      setError(message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
          {t('admin.dashboard.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#78909c' }}>
          {t('admin.dashboard.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('admin.dashboard.totalServers')}
            value={stats?.totalServers ?? 0}
            icon={<ServersIcon />}
            color="#1565c0"
            bgColor="#e3f2fd"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('admin.dashboard.totalClients')}
            value={stats?.totalClients ?? 0}
            icon={<ClientsIcon />}
            color="#2e7d32"
            bgColor="#e8f5e9"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('admin.dashboard.totalUsers')}
            value={stats?.totalUsers ?? 0}
            icon={<UsersIcon />}
            color="#e65100"
            bgColor="#fff3e0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('admin.dashboard.activeSessions')}
            value={stats?.activeSessions ?? 0}
            icon={<SessionsIcon />}
            color="#6a1b9a"
            bgColor="#f3e5f5"
          />
        </Grid>
        {(stats?.pendingSetupRequests ?? 0) > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title={t('admin.setupRequests.pendingSetupRequests')}
              value={stats?.pendingSetupRequests ?? 0}
              icon={<SetupIcon />}
              color="#f57f17"
              bgColor="#fff8e1"
            />
          </Grid>
        )}
      </Grid>

      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          variant="filled"
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
